import React, { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../../lib/supabaseClient";

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
      toast.type === "success" ? "bg-gray-900 text-white" :
      toast.type === "warning" ? "bg-amber-500 text-white" :
      "bg-red-600 text-white"
    }`}>
      {toast.message}
    </div>
  );
}

// ── Document Type Badge ───────────────────────────────────────────────────────

const DOC_TYPE_COLORS = {
  "Certificate":  "bg-green-100 text-green-700",
  "Licence":      "bg-blue-100 text-blue-700",
  "Induction":    "bg-purple-100 text-purple-700",
  "Assessment":   "bg-amber-100 text-amber-700",
  "Policy":       "bg-gray-100 text-gray-600",
  "Other":        "bg-gray-100 text-gray-500",
};

const DOC_TYPES = Object.keys(DOC_TYPE_COLORS);

function DocTypeBadge({ type }) {
  const color = DOC_TYPE_COLORS[type] || "bg-gray-100 text-gray-500";
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {type || "Other"}
    </span>
  );
}

// ── Format file size ──────────────────────────────────────────────────────────

function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Training Records Panel ────────────────────────────────────────────────────

function TrainingRecordsPanel({ user, onClose, onDocumentChange }) {
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("Certificate");
  const [toast, setToast] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  function showToast(message, type = "success") {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  const loadDocuments = useCallback(async () => {
    setDocsLoading(true);
    const { data, error } = await supabase
      .from("user_documents")
      .select("*")
      .eq("user_id", user.user_id)
      .order("uploaded_at", { ascending: false });
    if (!error) setDocuments(data || []);
    setDocsLoading(false);
  }, [user.user_id]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  async function handleUpload(file) {
    if (!file) return;
    if (file.type !== "application/pdf") {
      showToast("Only PDF files are supported.", "error");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast("File must be under 10MB.", "error");
      return;
    }

    setUploading(true);
    try {
      // Get the currently logged-in admin/user
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      if (authError || !authUser) throw new Error("Not authenticated.");

      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `${user.user_id}/${timestamp}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("user-documents")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase
        .from("user_documents")
        .insert({
          user_id: user.user_id,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          document_type: docType,
          uploaded_by: authUser.id,
        });

      if (dbError) throw dbError;

      showToast(`${file.name} uploaded.`);
      loadDocuments();
      onDocumentChange?.();
    } catch (err) {
      showToast(err.message || "Upload failed.", "error");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDownload(doc) {
    try {
      const { data, error } = await supabase.storage
        .from("user-documents")
        .createSignedUrl(doc.file_path, 60);
      if (error) throw error;
      window.open(data.signedUrl, "_blank");
    } catch (err) {
      showToast("Could not generate download link.", "error");
    }
  }

  async function handleDelete(doc) {
    if (!window.confirm(`Delete "${doc.file_name}"?`)) return;
    try {
      const { error: storageError } = await supabase.storage
        .from("user-documents")
        .remove([doc.file_path]);
      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from("user_documents")
        .delete()
        .eq("id", doc.id);
      if (dbError) throw dbError;

      showToast(`${doc.file_name} deleted.`);
      loadDocuments();
      onDocumentChange?.();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  }

  if (!user) return null;

  return (
    <>
      <Toast toast={toast} />
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/20 z-30" onClick={onClose} />
      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-[480px] bg-white shadow-2xl z-40 flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {user.first_name} {user.last_name}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Training Records</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 space-y-6" style={{ maxHeight: "calc(100vh - 80px)" }}>

          {/* Upload Section */}
          <div className="space-y-3">
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Upload Document</label>

            {/* Document Type Selector */}
            <div className="flex flex-wrap gap-2">
              {DOC_TYPES.map(t => (
                <button
                  key={t}
                  onClick={() => setDocType(t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                    docType === t
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* Drop Zone */}
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-xl px-6 py-8 text-center cursor-pointer transition-all ${
                dragOver
                  ? "border-gray-900 bg-gray-50"
                  : "border-gray-200 hover:border-gray-400 hover:bg-gray-50"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={e => handleUpload(e.target.files[0])}
              />
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-800" />
                  <p className="text-sm text-gray-500">Uploading...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm text-gray-500">
                    <span className="font-medium text-gray-700">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-400">PDF only · max 10MB</p>
                </div>
              )}
            </div>
          </div>

          {/* Documents List */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Documents ({documents.length})
            </label>

            {docsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-800" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400 border border-gray-100 rounded-xl">
                No documents uploaded yet
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map(doc => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex-shrink-0 w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{doc.file_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <DocTypeBadge type={doc.document_type} />
                          <span className="text-xs text-gray-400">{formatSize(doc.file_size)}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(doc.uploaded_at).toLocaleDateString("en-AU", {
                              day: "numeric", month: "short", year: "numeric"
                            })}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleDownload(doc)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        title="Download"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(doc)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function TrainingRecords() {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [docCounts, setDocCounts] = useState({});

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    const { data, error } = await supabase
      .from("profiles")
      .select("user_id, first_name, last_name, email, role, groups, active")
      .order("last_name", { ascending: true });
    if (!error) setUsers(data || []);
    setUsersLoading(false);
  }, []);

  const loadDocCounts = useCallback(async () => {
    const { data, error } = await supabase
      .from("user_documents")
      .select("user_id");
    if (!error && data) {
      const counts = {};
      data.forEach(row => {
        counts[row.user_id] = (counts[row.user_id] || 0) + 1;
      });
      setDocCounts(counts);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadDocCounts();
  }, [loadUsers, loadDocCounts]);

  const filteredUsers = users.filter(u => {
    const str = `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase();
    return str.includes(search.toLowerCase());
  });

  const ROLE_LABELS = { learner: "Learner", manager: "Manager", superadmin: "Super Admin" };
  const ROLE_COLORS = {
    learner: "bg-gray-100 text-gray-600",
    manager: "bg-blue-100 text-blue-700",
    superadmin: "bg-purple-100 text-purple-700",
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Training Records</h1>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-800">Select a User</h3>
            <p className="text-xs text-gray-400 mt-0.5">{users.length} users · click a row to view records</p>
          </div>
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-800 w-56"
          />
        </div>

        {usersLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-800" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Documents</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u, i) => (
                  <tr
                    key={u.user_id}
                    onClick={() => setSelectedUser(u)}
                    className={`border-b border-gray-50 cursor-pointer transition-colors hover:bg-blue-50/40 ${
                      selectedUser?.user_id === u.user_id ? "bg-blue-50" : i % 2 === 0 ? "bg-white" : "bg-gray-50/30"
                    }`}
                  >
                    <td className="px-6 py-3 font-medium text-gray-800 whitespace-nowrap">
                      {u.first_name} {u.last_name}
                    </td>
                    <td className="px-6 py-3 text-gray-500 whitespace-nowrap">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[u.role] || "bg-gray-100 text-gray-600"}`}>
                        {ROLE_LABELS[u.role] || u.role || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {docCounts[u.user_id] ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700">
                          <svg className="w-3.5 h-3.5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                          {docCounts[u.user_id]} file{docCounts[u.user_id] !== 1 ? "s" : ""}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-10 text-center text-sm text-gray-400">No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedUser && (
        <TrainingRecordsPanel
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onDocumentChange={loadDocCounts}
        />
      )}
    </div>
  );
}
